import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import {
  updateAboutDetails,
  getAboutDetails,
  uploadAboutFacilities,
  deleteAboutFacility,
} from "./services/about.services";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import FormTextField from "../../components/formFields/FormTextField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormTextArea from "../../components/formFields/FormTextArea";
import {
  requiredArrayValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import IconButton from "../../components/common/IconButton";
import { FiEdit } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import {
  clearAboutFacilityDetails,
  clearFacilityID,
  setAboutFacilityDetails,
  setFacilityID,
} from "./aboutSlice";
import { Button } from "@progress/kendo-react-buttons";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";

const About: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const [formKey, setFormKey] = React.useState(1);

  const loading = useAppSelector((state) => state.about.loading);
  const AboutDetails = useAppSelector((state) => state.about.AboutDetails);

  useEffect(() => {
    dispatch(getAboutDetails());
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [AboutDetails]);

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const AboutImage =
      values?.AboutImage && typeof values?.AboutImage !== "string"
        ? values.AboutImage[0].getRawFile()
        : "";

    try {
      ImageData.append("AboutImage", AboutImage);
      ImageData.append("Title", values?.Title);
      ImageData.append("Description", values?.Description);
      ImageData.append("Experience", values?.Experience || "");
      ImageData.append("AlterTag", values?.AlterTag);

      const res = await dispatch(updateAboutDetails(ImageData));
      if (res.meta?.requestStatus === "fulfilled") {
        dispatch(getAboutDetails());
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={AboutDetails}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <Typography.h4>{"About Setting"}</Typography.h4>
            <ShadowCard style={{ padding: 12, margin: "20px 0" }}>
              <GridLayout
                gap={{ rows: 10, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
                style={{ marginRight: 20 }}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h5
                    style={{
                      marginBottom: "6px",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    About Shree Swaminarayan Tourism
                  </Typography.h5>
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    wrapperClassName="w-100"
                    name={`Title`}
                    label="Title"
                    placeholder="Title"
                    component={FormTextField}
                    astrike={true}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="w-100"
                    name={`Experience`}
                    label="Experience"
                    placeholder="Experience"
                    component={FormNumericTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="w-100"
                    name={`Description`}
                    label="Description"
                    placeholder="Description"
                    component={FormTextArea}
                    rows={6}
                  />
                </GridLayoutItem>
                <GridLayoutItem className="d-flex flex-column gap-2">
                  <Field
                    name={`AboutImage`}
                    label="About Image"
                    type="file"
                    accept="image/*"
                    alternateName="About Image"
                    component={FormImageUpload}
                  />
                  {/* </GridLayoutItem>
                <GridLayoutItem> */}
                  <Field
                    wrapperClassName="w-100"
                    name={`AlterTag`}
                    label="About Alt. Text"
                    placeholder="About Alt. Text"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem className="d-flex justify-content-end align-items-end">
                  {/* <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginRight: 10 }}
                  >
                    Cancel
                  </Button> */}
                  <ButtonWithLoading
                    label={"Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
      <AboutFacility />
    </>
  );
};

const AboutFacility: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const [formKey, setFormKey] = React.useState(1);

  const loading = useAppSelector((state) => state.about.loading);
  const AboutDetails = useAppSelector((state) => state.about.AboutDetails);
  const AboutFacilityDetails = useAppSelector(
    (state) => state.about.AboutFacilityDetails
  );

  useEffect(() => {
    dispatch(getAboutDetails());
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [AboutFacilityDetails]);

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const Icon =
      values?.FacilityIcon && typeof values?.FacilityIcon !== "string"
        ? values?.FacilityIcon[0].getRawFile()
        : "";

    try {
      ImageData.append("ID", values?.ID ? values?.ID : "0");
      ImageData.append("FacilityIcon", Icon);
      ImageData.append("Title", values?.FacilityTitle);

      const res = await dispatch(uploadAboutFacilities(ImageData));
      if (res.meta?.requestStatus === "fulfilled") {
        dispatch(getAboutDetails());
        dispatch(clearAboutFacilityDetails());
        setFormKey(formKey + 1);
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleEdit = (item: any) => {
    const payload = {
      ID: item?.ID,
      FacilityTitle: item?.Title,
      FacilityIcon: item?.FacilityIcon,
    };
    dispatch(setAboutFacilityDetails(payload));
  };

  const handleOpenDeleteDialog = (ID: number) => {
    dispatch(openDialog("deleteAboutFacility"));
    dispatch(setFacilityID(ID));
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <DeleteTourRateDialog />
      <ShadowCard style={{ padding: 12, margin: "20px 0" }}>
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={AboutFacilityDetails}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout
                gap={{ rows: 10, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "43.66%" },
                  { width: "23%" },
                ]}
                style={{ marginRight: 20 }}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h5
                    style={{
                      marginBottom: "6px",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    Facilities
                  </Typography.h5>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="w-100"
                    name={`FacilityTitle`}
                    label="Facility Title"
                    placeholder="Facility Title"
                    component={FormTextField}
                    astrike={true}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    justifyContent="start"
                    heightWidth={60}
                    name={`FacilityIcon`}
                    label="Facility Icon"
                    type="file"
                    accept="image/*"
                    alternateName="Facility Icon Image"
                    component={FormImageUpload}
                    validator={requiredArrayValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem className="d-flex justify-content-end align-items-end">
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginRight: 10 }}
                    onClick={() => dispatch(clearAboutFacilityDetails())}
                  >
                    Clear
                  </Button>
                  <ButtonWithLoading
                    label={formRenderProps.valueGetter("ID") ? "Update" : "Add"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
        <table className="about-table mt-4">
          <thead>
            <tr>
              <th style={{ width: 80 }}>Icon</th>
              <th>Title</th>
              <th style={{ width: 110 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {AboutDetails?.FacilityList &&
              AboutDetails?.FacilityList.length > 0 &&
              AboutDetails?.FacilityList.map((item: any, index: number) => (
                <tr key={index}>
                  <td>
                    <img
                      src={item?.FacilityIcon}
                      onClick={() =>
                        item?.FacilityIcon && window.open(item?.FacilityIcon)
                      }
                    />
                  </td>
                  <td>{item?.Title}</td>
                  <td>
                    <div className="d-flex justify-content-center">
                      <IconButton
                        type="button"
                        fillMode="flat"
                        size="small"
                        title="Edit"
                        onClick={() => handleEdit(item)}
                      >
                        <FiEdit
                          className="absolute-position"
                          style={{ fontSize: "20px" }}
                        />
                      </IconButton>

                      <IconButton
                        type="button"
                        fillMode="flat"
                        size="small"
                        title="Delete"
                        className="color-red"
                        onClick={() => handleOpenDeleteDialog(item.ID)}
                      >
                        <MdDelete
                          className="absolute-position"
                          style={{ fontSize: "24px" }}
                        />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </ShadowCard>
    </>
  );
};

const DeleteTourRateDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const FacilityID = useAppSelector((state) => state.about.FacilityID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteAboutFacility(ID));
      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(getAboutDetails());
        dispatch(closeDialog());
        dispatch(clearFacilityID());
      } else {
        dispatch(getAboutDetails());
        dispatch(closeDialog());
        dispatch(clearFacilityID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteAboutFacility" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Facility"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this facility?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(FacilityID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default About;
